import { emit } from '../../methods/events';
import type { BridgeDataToForward, FAUSHandledBridgeMethods } from './types';

const EMIT_READY_EVENT_NAME = 'EXTENSION_READY';
const UPDATE_BYLINE_ITEM_EVENT_NAME = 'FORGE_CONTENT_PROPERTY_UPDATED';

export const isNonNativeConfluenceEmitEvent = (eventKey: string, eventName: string | undefined): boolean => {
	if (!eventName) {
		return false;
	}

	return (
		eventKey === 'emit' &&
		!(eventName === EMIT_READY_EVENT_NAME || eventName === UPDATE_BYLINE_ITEM_EVENT_NAME)
	);
};

// Creates a forwarder that sends messages to the product (via window.parent.parent)
// Used by FAUS to forward bridge calls such as showFlag/closeFlag/openModal calls from nested apps to confluence which controls the outcome state
export const createMessageForwarder =
	(
		eventKey: FAUSHandledBridgeMethods,
		dataToForward: BridgeDataToForward,
		postRobotSend: any | null,
		embeddedParentLocalId: string | undefined,
		extensionDetails: {
			appId: string;
			id: string;
			installationId: string | undefined;
		},
	) =>
	async ({ data }: any): Promise<any> => {
		// emitReadyEvent method calls the event method and also the actual emitReadyEvent method.
		// In use bridge we have 'emit' method and 'emitReadyEvent' method
		// This is referring to the former. For the latter, event key will be 'emitReadyEvent'
		if (isNonNativeConfluenceEmitEvent(eventKey, data?.event)) {
			// Emit event as usual unless it is emitReadyEvent or updateBylineProperties event
			emit(data?.event, data?.payload, extensionDetails);
			return;
		}
		if (!postRobotSend || !embeddedParentLocalId) {
			return;
		}

		// This would need to be changed if we want to support multiple nesting levels
		const targetWindow = window.parent?.parent;

		if (!targetWindow || targetWindow === window) {
			return;
		}

		// Only forward to same hostname as FAUS for security (allow different ports for local dev)
		const targetDomain =
			process.env.NODE_ENV === 'development'
				? '*'
				: `${window.location.protocol}//${window.location.hostname}`;

		// Check if data is an object or not.
		// E.g. updateMacroContent method's data is a type string.
		const originalPayload = typeof data === 'object' ? data : { data };

		const forwardedData: Record<string, any> = {
			eventKey,
			...dataToForward,
			...originalPayload,
		};

		try {
			const { data } = await postRobotSend(
				targetWindow,
				`messageFromEmbeddedFAUS_${embeddedParentLocalId}`,
				forwardedData,
				{
					domain: targetDomain,
				},
			);
			return data;
		} catch (error: unknown) {
			// eslint-disable-next-line no-console
			console.error(`Failed to forward ${eventKey} to product:`, error);
		}
	};
